import { useState } from 'react';
import { Eye } from 'lucide-react';
import { operatorConsole, type PackBuilderAssetSlot } from '../../../lib/api/operatorConsole';
import { Button } from '../../../components/ui/Button';
import { ImageSlot } from '../../Configs/ImageSlot';
import { PreviewModal } from '../PreviewModal';
import { SUPPORT_DISABLED_HINT, isSupportDisabled } from '../packBuilderFormat';
import type { PackTabProps } from './packTabProps';

const IMAGE_SLOTS: { key: PackBuilderAssetSlot; label: string }[] = [
  { key: 'bg_image', label: 'Background' },
  { key: 'pack_image', label: 'Pack' },
  { key: 'pack_logo_image', label: 'Logo' },
  { key: 'pedestal_image', label: 'Pedestal' },
];

export function AssetsTab({ pack, onPackChange, notify }: PackTabProps) {
  const [previewOpen, setPreviewOpen] = useState(false);
  // PATCH assets and DELETE assets/:slot both answer 409 for a pack support
  // has disabled — the preview stays, the writes go.
  const readOnly = isSupportDisabled(pack);

  async function handleUpload(slot: PackBuilderAssetSlot, file: File) {
    try {
      const updated = await operatorConsole.packBuilder.uploadAsset(pack.id, slot, file);
      onPackChange(updated);
      notify('success', 'Image uploaded');
    } catch (err) {
      notify('error', `Upload failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  async function handleClear(slot: PackBuilderAssetSlot) {
    try {
      await operatorConsole.packBuilder.deleteAsset(pack.id, slot);
      const images = { ...pack.images };
      images[`${slot}_url`] = null;
      onPackChange({ ...pack, images });
      notify('success', 'Image removed');
    } catch (err) {
      notify('error', `Remove failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-[12px] text-[var(--co-muted)]">
          {readOnly
            ? SUPPORT_DISABLED_HINT
            : 'Artwork players see in the iframe. Empty slots fall back to the template defaults.'}
        </p>
        <Button
          variant="ghost"
          onClick={() => setPreviewOpen(true)}
          className="gap-2 px-3 py-1.5 text-[12px]"
        >
          <Eye size={14} aria-hidden="true" />
          Preview
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {IMAGE_SLOTS.map(({ key, label }) => {
          const imageUrl = pack.images[`${key}_url`];
          return (
            <ImageSlot
              key={key}
              label={label}
              imageUrl={imageUrl}
              isOverride={imageUrl !== null}
              readOnly={readOnly}
              onUpload={(file) => handleUpload(key, file)}
              onClear={() => handleClear(key)}
            />
          );
        })}
      </div>

      <PreviewModal pack={pack} open={previewOpen} onClose={() => setPreviewOpen(false)} />
    </div>
  );
}
